import { useState, useEffect, useRef } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";

export default function GameSection() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [playerY, setPlayerY] = useState(0);
  const [obstacles, setObstacles] = useState<{ x: number; id: number; scored: boolean; height: number }[]>([]);
  const [isJumping, setIsJumping] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  const gameRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  const obstacleIdRef = useRef(0);
  const velocityRef = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const PLAYER_SIZE = 130;
  const OBSTACLE_WIDTH = 30;
  const MIN_OBSTACLE_HEIGHT = 40;
  const MAX_OBSTACLE_HEIGHT = 65;
  const GROUND_HEIGHT = 5;
  const JUMP_FORCE = 18;
  const GRAVITY = 0.85;
  const GAME_SPEED = 5;
  const MIN_OBSTACLE_DISTANCE = 400;
  const MAX_OBSTACLE_DISTANCE = 700;
  const COLLISION_OFFSET = 40;

  const startGame = () => {
    setGameOver(false);
    setScore(0);
    setPlayerY(0);
    setObstacles([]);
    setIsJumping(false);
    setIsPaused(false);
    obstacleIdRef.current = 0;
    velocityRef.current = 0;
    setCountdown(3);
  };

  const togglePause = () => {
    setIsPaused((prev) => !prev);
  };

  const restartGame = () => {
    startGame();
  };

  const jump = () => {
    if (!isPlaying || gameOver) {
      if (gameOver) {
        startGame();
      }
      return;
    }
    if (isPaused) return;
    if (!isJumping && playerY === 0) {
      velocityRef.current = JUMP_FORCE;
      setIsJumping(true);
    }
  };

  useEffect(() => {
    if (!isPlaying || gameOver || isPaused) return;

    const gameLoop = () => {
      if (isJumping || playerY > 0) {
        velocityRef.current -= GRAVITY;
        const newY = playerY + velocityRef.current;

        if (newY <= 0) {
          setPlayerY(0);
          velocityRef.current = 0;
          setIsJumping(false);
        } else {
          setPlayerY(newY);
        }
      }

      setObstacles((prev) => {
        const newObstacles = prev
          .map((obs) => ({ ...obs, x: obs.x - GAME_SPEED }))
          .filter((obs) => obs.x > -OBSTACLE_WIDTH);

        const gameWidth = gameRef.current?.offsetWidth || 800;
        const lastObstacle = newObstacles[newObstacles.length - 1];
        
        if (newObstacles.length === 0) {
          const randomHeight = MIN_OBSTACLE_HEIGHT + Math.random() * (MAX_OBSTACLE_HEIGHT - MIN_OBSTACLE_HEIGHT);
          newObstacles.push({
            x: gameWidth,
            id: obstacleIdRef.current++,
            scored: false,
            height: randomHeight,
          });
        } else if (lastObstacle) {
          const distanceFromRight = gameWidth - lastObstacle.x;
          const randomDistance = MIN_OBSTACLE_DISTANCE + Math.random() * (MAX_OBSTACLE_DISTANCE - MIN_OBSTACLE_DISTANCE);
          
          if (distanceFromRight > randomDistance) {
            const randomHeight = MIN_OBSTACLE_HEIGHT + Math.random() * (MAX_OBSTACLE_HEIGHT - MIN_OBSTACLE_HEIGHT);
            newObstacles.push({
              x: gameWidth,
              id: obstacleIdRef.current++,
              scored: false,
              height: randomHeight,
            });
          }
        }

        const playerLeft = gameWidth * 0.1 + COLLISION_OFFSET;
        const playerRight = playerLeft + PLAYER_SIZE - COLLISION_OFFSET;
        const playerBottom = playerY;
        const playerTop = playerBottom + PLAYER_SIZE;

        for (const obs of newObstacles) {
          const obsLeft = obs.x;
          const obsRight = obs.x + OBSTACLE_WIDTH;
          const obsBottom = GROUND_HEIGHT;
          const obsTop = GROUND_HEIGHT + obs.height;

          if (
            playerRight > obsLeft &&
            playerLeft < obsRight &&
            playerBottom < obsTop &&
            playerTop > obsBottom
          ) {
            setIsPlaying(false);
            setGameOver(false);
            setScore(0);
            setPlayerY(0);
            setObstacles([]);
            setIsJumping(false);
            setIsPaused(false);
            obstacleIdRef.current = 0;
            velocityRef.current = 0;
            if (audioRef.current) {
              audioRef.current.currentTime = 0;
              audioRef.current.play().catch(() => {});
            }
            return [];
          }

          if (!obs.scored && obs.x + OBSTACLE_WIDTH < playerLeft) {
            obs.scored = true;
            setScore((s) => s + 1);
          }
        }

        return newObstacles;
      });

      animationRef.current = requestAnimationFrame(gameLoop);
    };

    animationRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, gameOver, isJumping, playerY, isPaused]);

  useEffect(() => {
    if (countdown === null) return;

    if (countdown === 0) {
      setCountdown(null);
      setIsPlaying(true);
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.code === "Space" || e.code === "ArrowUp") {
        e.preventDefault();
        jump();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isPlaying, gameOver, isJumping, isPaused, playerY]);

  return (
    <section 
      className="relative"
      style={{ 
        backgroundColor: "#DA422F",
        backgroundImage: "url(/images/bg-game.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        height: "70vh"
      }}
    >
      <div className="relative w-full h-full">
        <div
          ref={gameRef}
          onClick={jump}
          className="absolute inset-0 overflow-hidden cursor-pointer"
          style={{ backgroundColor: "transparent" }}
        >
          <div
            className="absolute left-0 right-0 bottom-0"
            style={{ 
              height: `${GROUND_HEIGHT}px`, 
              backgroundColor: "#F8EBC1"
            }}
          />

          <div
            className="absolute transition-transform duration-75"
            style={{
              width: `${PLAYER_SIZE}px`,
              height: `${PLAYER_SIZE}px`,
              left: "10%",
              bottom: `${playerY + GROUND_HEIGHT}px`,
              transform: isJumping ? "rotate(-10deg)" : "rotate(0deg)",
            }}
          >
            <img
              src="/images/game.png"
              alt="Player"
              className="w-full h-full object-contain object-bottom"
              style={{ imageRendering: "crisp-edges" }}
            />
          </div>

          {obstacles.map((obs) => (
            <div
              key={obs.id}
              className="absolute"
              style={{
                width: `${OBSTACLE_WIDTH}px`,
                height: `${obs.height}px`,
                backgroundColor: "#F8EBC1",
                left: `${obs.x}px`,
                bottom: `${GROUND_HEIGHT}px`,
              }}
            />
          ))}

          {isPlaying && !gameOver && (
            <div className="absolute top-4 left-4 flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  togglePause();
                }}
                className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center transition hover:scale-110"
                style={{ backgroundColor: "#F8EBC1", color: "#DA422F" }}
                aria-label={isPaused ? "Retomar" : "Pausar"}
              >
                {isPaused ? (
                  <Play className="w-5 h-5 md:w-6 md:h-6" fill="#DA422F" />
                ) : (
                  <Pause className="w-5 h-5 md:w-6 md:h-6" />
                )}
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  restartGame();
                }}
                className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center transition hover:scale-110"
                style={{ backgroundColor: "#F8EBC1", color: "#DA422F" }}
                aria-label="Reiniciar"
              >
                <RotateCcw className="w-5 h-5 md:w-6 md:h-6" />
              </button>
            </div>
          )}

          {isPlaying && (
            <div
              className="absolute top-4 right-4 text-3xl md:text-4xl font-bold"
              style={{ color: "#F8EBC1", fontFamily: "var(--font-oswald)" }}
            >
              {score}
            </div>
          )}

          {isPlaying && isPaused && !gameOver && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center space-y-4 px-6">
                <h3
                  className="text-4xl md:text-6xl font-bold uppercase"
                  style={{ color: "#F8EBC1", fontFamily: "var(--font-oswald)" }}
                >
                  PAUSADO
                </h3>
                <p className="text-xl md:text-2xl font-bold" style={{ color: "#F8EBC1" }}>
                  Score: {score}
                </p>
                <button
                  onClick={togglePause}
                  className="px-8 py-4 text-lg md:text-xl font-bold uppercase tracking-wider transition hover:scale-105 rounded-lg"
                  style={{ backgroundColor: "#F8EBC1", color: "#DA422F" }}
                >
                  Continuar
                </button>
              </div>
            </div>
          )}

          {!isPlaying && !countdown && (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <h2
                className="text-4xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tight mb-8"
                style={{ color: "#F8EBC1", fontFamily: "var(--font-oswald)" }}
              >
                DU NADA GAME
              </h2>
              <button
                onClick={startGame}
                className="w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center transition hover:scale-110 shadow-2xl mx-auto"
                style={{ backgroundColor: "#F8EBC1", color: "#DA422F" }}
                aria-label="Iniciar Jogo"
              >
                <Play className="w-12 h-12 md:w-14 md:h-14 ml-2" fill="#DA422F" />
              </button>
            </div>
          )}

          {countdown !== null && countdown > 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="text-9xl md:text-[12rem] font-bold animate-pulse"
                style={{ color: "#F8EBC1", fontFamily: "var(--font-oswald)" }}
              >
                {countdown}
              </div>
            </div>
          )}
        </div>
      </div>

      <audio ref={audioRef} src="/images/game-over.mp3" preload="auto" />
    </section>
  );
}
